import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useNotificationStore } from '@/store/notificationStore';

export interface NotificationCounts {
  unreadMessages: number;
  pendingRequests: number;
}

export function useNotifications(): NotificationCounts {
  const { user } = useAuth();
  const { unreadMessages, pendingRequests, fetchUnreadMessages, fetchPendingRequests } = useNotificationStore();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userIdRef = useRef<string | null>(null);

  // Debounced refetch — batches rapid realtime events into one query
  function debouncedRefetch(userId: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchUnreadMessages(userId), 400);
  }

  // Realtime subscription — only used for INCOMING messages from OTHER clients
  // (same-client changes are handled directly in Chat.tsx via the store action)
  function subscribe(userId: string) {
    if (channelRef.current) supabase.removeChannel(channelRef.current);
    const channel = supabase
      .channel(`notifications:${userId}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        () => debouncedRefetch(userId)
      )
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'friendships' },
        (payload) => {
          const req = payload.new as { addressee_id: string; status: string };
          if (req.addressee_id === userId && req.status === 'pending') {
            fetchPendingRequests(userId);
          }
        }
      )
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'friendships' },
        (payload) => {
          const row = payload.new as { addressee_id: string };
          if (row.addressee_id === userId) fetchPendingRequests(userId);
        }
      )
      .subscribe();
    channelRef.current = channel;
  }

  // visibilitychange fallback — refetch when user returns to the tab
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden && userIdRef.current) {
        fetchUnreadMessages(userIdRef.current);
        fetchPendingRequests(userIdRef.current);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchUnreadMessages, fetchPendingRequests]);

  useEffect(() => {
    if (!user) {
      useNotificationStore.setState({ unreadMessages: 0, pendingRequests: 0 });
      userIdRef.current = null;
      return;
    }
    userIdRef.current = user.id;
    fetchUnreadMessages(user.id);
    fetchPendingRequests(user.id);
    subscribe(user.id);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return { unreadMessages, pendingRequests };
}
